/**
 * SSE Client for stateless chat streaming
 *
 * Connects to the stateless chat SSE endpoint and handles:
 * - Auth token refresh on 401/419
 * - Automatic reconnection with exponential backoff
 * - Sequence-based resumption via Last-Event-ID
 * - Terminal event detection (done/failed/cancelled)
 */

import { tokenService } from '../../../services/tokenService';

// Reconnect configuration
const MAX_RECONNECT_ATTEMPTS = 5;
const RECONNECT_BASE_DELAY_MS = 1000;
const RECONNECT_MAX_DELAY_MS = 30000;
const MAX_AUTH_RETRIES = 3;

/** SEM event envelope received from the backend */
export interface SEMEvent {
  sem?: boolean;
  event?: {
    type: string;
    id: string;
    [key: string]: unknown;
  };
}

/** Terminal event types that signal end of stream */
export type TerminalEventType = 'done' | 'failed' | 'cancelled';

/** Terminal event structure */
export interface TerminalEvent {
  type: TerminalEventType;
  data: Record<string, unknown>;
}

export interface StreamFrameMetadata {
  runId: string;
  seq?: number;
}

/** Options for SSE connection */
export interface SSEConnectionOptions {
  runId: string;
  initialAfterSeq?: number;
  onEvent: (event: SEMEvent, meta: StreamFrameMetadata) => void;
  onTerminal: (event: TerminalEvent, meta: StreamFrameMetadata) => void;
  onError: (error: Error) => void;
  onConnected?: () => void;
  onDisconnected?: () => void;
}

/** Result of SSE connection */
export interface SSEConnection {
  abort: () => void;
  getLastSeq: () => number;
}

/**
 * Redirect to login page (called when auth refresh exhausted)
 */
function redirectToLogin(): void {
  // Use the app's existing auth flow
  window.location.href = '/login';
}

/**
 * Fetch run state from backend for terminal detection fallback
 */
async function fetchRunState(runId: string, signal: AbortSignal): Promise<string | null> {
  try {
    const token = await tokenService.getActiveToken();
    if (!token) return null;

    const resp = await fetch(`/rpc/v1/chat/runs/${runId}`, {
      headers: { Authorization: `Bearer ${token}` },
      signal,
    });

    if (!resp.ok) return null;
    const body = (await resp.json()) as { state?: string };
    return typeof body?.state === 'string' ? body.state : null;
  } catch {
    return null;
  }
}

type RunTerminalState = 'completed' | 'failed' | 'cancelled';

function toTerminalState(state: string | null): RunTerminalState | null {
  if (state === 'completed' || state === 'failed' || state === 'cancelled') {
    return state;
  }
  return null;
}

function toTerminalEventType(state: RunTerminalState): TerminalEventType {
  if (state === 'completed') {
    return 'done';
  }
  return state;
}

/**
 * Connect to the SSE endpoint for a chat run
 *
 * @param options Connection options
 * @returns Connection handle with abort method
 */
export function connectToSSE(options: SSEConnectionOptions): SSEConnection {
  const {
    runId,
    initialAfterSeq = 0,
    onEvent,
    onTerminal,
    onError,
    onConnected,
    onDisconnected,
  } = options;

  const controller = new AbortController();
  let afterSeq = initialAfterSeq;
  let reconnectAttempts = 0;
  let isAborted = false;

  const connect = async (authRetryCount = 0): Promise<void> => {
    if (isAborted) return;

    const token = await tokenService.getActiveToken();
    if (!token) {
      onError(new Error('No authentication token available'));
      return;
    }

    try {
      // Build headers - include Last-Event-ID for reconnect resume per SSE spec
      const headers: Record<string, string> = {
        Accept: 'text/event-stream',
        Authorization: `Bearer ${token}`,
      };
      // Last-Event-ID header is the standard SSE mechanism for resumption.
      // We also include after_seq as query param for compatibility.
      if (afterSeq > 0) {
        headers['Last-Event-ID'] = String(afterSeq);
      }

      const response = await fetch(`/rpc/v1/chat/runs/${runId}/events?after_seq=${afterSeq}`, {
        headers,
        signal: controller.signal,
      });

      // === AUTH REFRESH HANDLING ===
      if (response.status === 401 || response.status === 419) {
        if (authRetryCount >= MAX_AUTH_RETRIES) {
          onError(new Error('Authentication failed after retries'));
          redirectToLogin();
          return;
        }

        try {
          // Invalidate current access token to force refresh on next getAccessToken() call.
          // Without this, getAccessToken() may return the same invalid token if it hasn't
          // expired according to the client-side JWT decode (but is invalid server-side).
          tokenService.invalidateAccessToken();
          // Now getAccessToken() will use refresh token to obtain a new access token
          const newToken = await tokenService.getAccessToken();
          // If refresh returns null (non-throwing failure), redirect to login
          if (!newToken) {
            onError(new Error('Token refresh failed'));
            redirectToLogin();
            return;
          }
          return connect(authRetryCount + 1);
        } catch {
          onError(new Error('Token refresh failed'));
          redirectToLogin();
          return;
        }
      }

      // Integrity mismatch: terminal run without persisted terminal event
      if (response.status === 409) {
        onError(new Error('terminal_event_missing'));
        return;
      }

      if (!response.ok) {
        throw new Error(`SSE connection failed: ${response.status}`);
      }

      // Reset reconnect counter on successful connection
      reconnectAttempts = 0;
      onConnected?.();

      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      // SSE frame parser state
      let currentId: number | null = null;
      let currentEvent: string | null = null;
      let currentData: string | null = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // SSE spec allows \r\n, \n, or \r as line endings
        // Normalize by replacing \r\n with \n, then \r with \n
        buffer = buffer.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          // Trim trailing whitespace
          const trimmedLine = line.trimEnd();

          if (trimmedLine === '') {
            // Empty line = end of event frame
            if (currentData !== null) {
              // Sequence is in the id: field (per SSE contract)
              if (currentId !== null) {
                afterSeq = currentId;
              }

              // Check for terminal events
              if (
                currentEvent === 'done' ||
                currentEvent === 'failed' ||
                currentEvent === 'cancelled'
              ) {
                try {
                  const data = JSON.parse(currentData) as Record<string, unknown>;
                  onTerminal(
                    { type: currentEvent as TerminalEventType, data },
                    {
                      runId,
                      ...(currentId !== null ? { seq: currentId } : {}),
                    }
                  );
                } catch {
                  onTerminal(
                    { type: currentEvent as TerminalEventType, data: {} },
                    {
                      runId,
                      ...(currentId !== null ? { seq: currentId } : {}),
                    }
                  );
                }
                return; // Stream complete
              }

              // Normal SEM event
              try {
                const data = JSON.parse(currentData) as SEMEvent;
                onEvent(data, {
                  runId,
                  ...(currentId !== null ? { seq: currentId } : {}),
                });
              } catch {
                // Ignore malformed JSON
              }
            }
            // Reset for next frame
            currentId = null;
            currentEvent = null;
            currentData = null;
          } else if (trimmedLine.startsWith('id: ')) {
            // Extract sequence from id: field
            currentId = parseInt(trimmedLine.slice(4), 10);
          } else if (trimmedLine.startsWith('event: ')) {
            currentEvent = trimmedLine.slice(7);
          } else if (trimmedLine.startsWith('data: ')) {
            // SSE spec: multiple data: lines are concatenated with \n
            const dataContent = trimmedLine.slice(6);
            if (currentData === null) {
              currentData = dataContent;
            } else {
              currentData += '\n' + dataContent;
            }
          }
          // Ignore comments (lines starting with :)
        }
      }

      // === RECONNECT HANDLING FOR CLEAN STREAM END ===
      // Stream ended without terminal event

      if (controller.signal.aborted) {
        return;
      }

      onDisconnected?.();

      // Deterministic fallback: query run status before reconnect
      const runState = await fetchRunState(runId, controller.signal);
      const terminalState = toTerminalState(runState);
      if (terminalState) {
        onTerminal(
          {
            type: toTerminalEventType(terminalState),
            data: {
              synthetic: true,
              state: terminalState,
              reason: 'stream_closed_terminal_state',
            },
          },
          { runId }
        );
        return;
      }

      // Reconnect with exponential backoff
      return attemptReconnect();
    } catch {
      // Check if we were intentionally aborted
      if (controller.signal.aborted) {
        return;
      }

      onDisconnected?.();

      // Reconnect with exponential backoff
      return attemptReconnect();
    }
  };

  // Shared reconnect logic with exponential backoff
  const attemptReconnect = async (): Promise<void> => {
    if (isAborted) return;

    if (reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
      reconnectAttempts++;
      const delay = Math.min(
        RECONNECT_BASE_DELAY_MS * 2 ** (reconnectAttempts - 1) + Math.random() * 1000,
        RECONNECT_MAX_DELAY_MS
      );
      console.log(
        `SSE disconnected, reconnecting in ${Math.round(delay)}ms (attempt ${reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS}), afterSeq=${afterSeq}`
      );
      await new Promise((resolve) => setTimeout(resolve, delay));

      if (isAborted) return;
      return connect();
    }

    // Max retries exceeded
    onError(new Error(`SSE reconnection failed after ${MAX_RECONNECT_ATTEMPTS} attempts`));
  };

  // Start connection
  connect().catch(onError);

  return {
    abort: () => {
      isAborted = true;
      controller.abort();
    },
    getLastSeq: () => afterSeq,
  };
}

/**
 * Create a new chat run and return the run ID
 *
 * @param params Run creation parameters
 * @returns Created run info
 */
export interface CreateRunParams {
  conversationId?: string;
  prompt: string;
  profileSlug?: string;
  messageEntrypoint: string;
  timezone?: string;
  clientTime?: string;
  clientMessageId?: string;
  draftBundleId?: string;
  overrides?: Record<string, unknown>;
  context?: Record<string, unknown>;
  formResponseId?: string; // Optional form_responses.id for member context injection (coach matching)
  promptOverride?: string; // Optional ephemeral prompt override (requires admin:coaches permission)
  idempotencyKey?: string;
}

export interface CreateRunResult {
  runId: string;
  conversationId: string;
}

/** Structured error thrown by createRun, preserving the HTTP status code. */
export class CreateRunError extends Error {
  status: number;
  activeRunId?: string;
  constructor(status: number, message: string, options?: { activeRunId?: string }) {
    super(message);
    this.name = 'CreateRunError';
    this.status = status;
    this.activeRunId = options?.activeRunId;
  }
}

export type ReplayCreateRunConflictError = CreateRunError & { activeRunId: string };

export function isReplayCreateRunConflict(err: unknown): err is ReplayCreateRunConflictError {
  if (!(err instanceof CreateRunError)) {
    return false;
  }
  if (err.status !== 409 || !err.activeRunId) {
    return false;
  }
  const msg = err.message.toLowerCase();
  return msg.includes('idempotency key') || msg.includes('existing request');
}

export async function createRun(params: CreateRunParams): Promise<CreateRunResult> {
  const idempotencyKey =
    params.idempotencyKey ||
    (params.clientMessageId
      ? `client-message:${params.clientMessageId}`
      : `run:${crypto.randomUUID()}`);
  return createRunWithAuthRetry(params, 0, idempotencyKey);
}

/**
 * Internal implementation with auth retry logic matching connectToSSE.
 * On 401/419, invalidates the cached token, refreshes via refresh token, and retries.
 */
async function createRunWithAuthRetry(
  params: CreateRunParams,
  authRetryCount: number,
  idempotencyKey: string
): Promise<CreateRunResult> {
  const token = await tokenService.getActiveToken();
  if (!token) {
    throw new Error('No authentication token available');
  }

  const response = await fetch('/rpc/v1/chat/runs', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      'Idempotency-Key': idempotencyKey,
    },
    body: JSON.stringify({
      conversation_id: params.conversationId,
      prompt: params.prompt,
      profile_slug: params.profileSlug || undefined,
      message_entrypoint: params.messageEntrypoint,
      timezone: params.timezone,
      client_time: params.clientTime,
      client_message_id: params.clientMessageId,
      draft_bundle_id: params.draftBundleId,
      overrides: params.overrides,
      context: params.context,
      form_response_id: params.formResponseId,
      prompt_override: params.promptOverride,
    }),
  });

  // Auth retry: invalidate + refresh + retry (same pattern as connectToSSE)
  if (response.status === 401 || response.status === 419) {
    if (authRetryCount >= MAX_AUTH_RETRIES) {
      redirectToLogin();
      throw new CreateRunError(response.status, 'Authentication failed after retries');
    }
    try {
      tokenService.invalidateAccessToken();
      const newToken = await tokenService.getAccessToken();
      if (!newToken) {
        redirectToLogin();
        throw new CreateRunError(response.status, 'Token refresh failed');
      }
      return createRunWithAuthRetry(params, authRetryCount + 1, idempotencyKey);
    } catch (err) {
      // Re-throw our own CreateRunError; wrap anything else
      if (err instanceof CreateRunError) throw err;
      redirectToLogin();
      throw new CreateRunError(response.status, 'Token refresh failed');
    }
  }

  if (!response.ok) {
    const rawText = await response.text().catch(() => 'Unknown error');
    let activeRunId: string | undefined;
    let normalizedMessage = rawText;
    try {
      const parsed = JSON.parse(rawText) as { error?: string; active_run_id?: string };
      if (typeof parsed.active_run_id === 'string' && parsed.active_run_id.trim()) {
        activeRunId = parsed.active_run_id;
      }
      if (typeof parsed.error === 'string' && parsed.error.trim()) {
        normalizedMessage = parsed.error;
      }
    } catch {
      // Not JSON; keep raw text.
    }
    throw new CreateRunError(
      response.status,
      `Failed to create run: ${response.status} ${normalizedMessage}`,
      { activeRunId }
    );
  }

  const result = (await response.json()) as { run_id: string; conversation_id: string };
  return {
    runId: result.run_id,
    conversationId: result.conversation_id,
  };
}

/**
 * Cancel a running chat run
 *
 * @param runId The run ID to cancel
 */
export async function cancelRun(runId: string): Promise<void> {
  const token = await tokenService.getActiveToken();
  if (!token) {
    throw new Error('No authentication token available');
  }

  const response = await fetch(`/rpc/v1/chat/runs/${runId}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok && response.status !== 404) {
    const errorText = await response.text().catch(() => 'Unknown error');
    throw new Error(`Failed to cancel run: ${response.status} ${errorText}`);
  }
}

/**
 * Get the current status of a run
 *
 * @param runId The run ID to check
 * @returns Run status info
 */
export interface RunStatus {
  id: string;
  state: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  lastEventSeq: number;
}

export async function getRunStatus(runId: string): Promise<RunStatus> {
  const token = await tokenService.getActiveToken();
  if (!token) {
    throw new Error('No authentication token available');
  }

  const response = await fetch(`/rpc/v1/chat/runs/${runId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to get run status: ${response.status}`);
  }

  const result = (await response.json()) as { id: string; state: string; last_event_seq: number };
  return {
    id: result.id,
    state: result.state as RunStatus['state'],
    lastEventSeq: result.last_event_seq,
  };
}
